import React from 'react'
import styled from 'styled-components'
import {
  ValidatedInputElement,
  ValidationType,
  Validation,
  RequiredValidation,
  MaximumValidation,
  MinimumValidation,
  RegexpValidation,
  MinimumLengthValidation,
  MaximumLengthValidation,
  MinimumDateValidation,
  MaximumDateValidation
} from '../../../common/types'
import {Required} from './required'
import {Minimum} from './minimum'
import {Maximum} from './maximum'
import {RegExp} from './regexp'
import {MaximumLength} from './maximumLength'
import {MinimumLength} from './minimumLength'
import {MinimumDate} from './minimumDate'
import {MaximumDate} from './maximumDate'

type ValidatedElementType = Pick<ValidatedInputElement, 'type'>['type']
type CompatibilityMap = {
  [K in ValidatedElementType]?: ValidationType[]
}

interface NodeValidatorsProps {
  node: ValidatedInputElement
  validations?: ValidatedInputElement['validations']
  updateValidations: (validations: ValidatedInputElement['validations']) => void
  readOnly?: boolean
}

const compatibilityMap: CompatibilityMap = {
  textInput: ['required', 'regexp', 'minimumLength', 'maximumLength'],
  numberInput: ['required', 'minimum', 'maximum'],
  dropdown: ['required'],
  datePicker: ['required', 'minimumDate', 'maximumDate'],
  stepper: ['required', 'minimum', 'maximum']
}

const NodeValidatorsContainer = styled.div`
  .validator-item:first-child {
    border-radius: ${p => p.theme.borderRadius} ${p => p.theme.borderRadius} 0 0;
  }
  .validator-item:last-child {
    border-bottom-right-radius: ${p => p.theme.borderRadius};
    border-bottom-left-radius: ${p => p.theme.borderRadius};
    border-bottom: 1px solid ${p => p.theme.grey10};
  }
  margin-bottom: 30px;
`

export const NodeValidators = ({
  node,
  validations,
  updateValidations,
  readOnly
}: NodeValidatorsProps) => {
  const updateValidatorAttributes = <V extends Validation>(
    type: ValidationType,
    attributes: V['attributes']
  ): void => {
    updateValidations({
      ...(validations ? validations : {}),
      [type]: {
        attributes
      }
    })
  }

  const getValidatorAttributes = <V extends Validation>(
    type: ValidationType
  ): V['attributes'] | undefined => {
    const typeValidation = validations ? validations[type] : null
    if (typeValidation) {
      return typeValidation.attributes
    }
    return
  }

  const disableValidation = (type: ValidationType) => {
    if (validations && type in validations) {
      const {[type]: omit, ...keep} = validations
      updateValidations(keep)
      // So the user can restore if desired
      localStorage.setItem(
        `validator-${type}-${node.key}`,
        JSON.stringify(omit)
      )
    }
  }

  const toggleValidation = <V extends Validation>(
    type: ValidationType,
    enabled: boolean,
    attributes: V['attributes']
  ) => {
    if (enabled) {
      updateValidatorAttributes<V>(type, attributes)
    } else {
      disableValidation(type)
    }
  }

  const getDefaultValues = <V extends Validation>(
    type: ValidationType,
    defaults: V['attributes']
  ): V['attributes'] => {
    const previous = localStorage.getItem(`validator-${type}-${node.key}`)
    if (previous) {
      try {
        const validation: V = JSON.parse(previous)
        return {
          ...defaults,
          ...validation.attributes
        }
      } catch (e) {
        console.warn('Unable to parse previous values')
      }
    }
    return defaults
  }

  const validators = compatibilityMap[node.type]
  if (!validators) {
    return null
  }
  return (
    <NodeValidatorsContainer>
      {validators.map(type => {
        switch (type) {
          case 'required': {
            const attributes = getValidatorAttributes<RequiredValidation>(type)
            return (
              <Required
                key={type}
                attributes={
                  attributes
                    ? attributes
                    : getDefaultValues<RequiredValidation>(type, {
                        required: true
                      })
                }
                updateAttributes={a =>
                  updateValidatorAttributes<RequiredValidation>(type, a)
                }
                enabled={!!attributes}
                toggleEnabled={(enabled, a) =>
                  toggleValidation(type, enabled, a)
                }
                readOnly={readOnly}
              />
            )
          }
          case 'minimum': {
            const attributes = getValidatorAttributes<MinimumValidation>(type)
            return (
              <Minimum
                key={type}
                attributes={
                  attributes
                    ? attributes
                    : getDefaultValues<MinimumValidation>(type, {minimum: 1})
                }
                updateAttributes={a =>
                  updateValidatorAttributes<MinimumValidation>(type, a)
                }
                enabled={!!attributes}
                toggleEnabled={(enabled, a) =>
                  toggleValidation(type, enabled, a)
                }
                readOnly={readOnly}
              />
            )
          }
          case 'maximum': {
            const attributes = getValidatorAttributes<MaximumValidation>(type)
            return (
              <Maximum
                key={type}
                attributes={
                  attributes
                    ? attributes
                    : getDefaultValues<MaximumValidation>(type, {maximum: 100})
                }
                updateAttributes={a =>
                  updateValidatorAttributes<MaximumValidation>(type, a)
                }
                enabled={!!attributes}
                toggleEnabled={(enabled, a) =>
                  toggleValidation(type, enabled, a)
                }
                readOnly={readOnly}
              />
            )
          }
          case 'minimumLength': {
            const attributes =
              getValidatorAttributes<MinimumLengthValidation>(type)
            return (
              <MinimumLength
                key={type}
                attributes={
                  attributes
                    ? attributes
                    : getDefaultValues<MinimumLengthValidation>(type, {
                        minimumLength: 1
                      })
                }
                updateAttributes={a =>
                  updateValidatorAttributes<MinimumLengthValidation>(type, a)
                }
                enabled={!!attributes}
                toggleEnabled={(enabled, a) =>
                  toggleValidation(type, enabled, a)
                }
                readOnly={readOnly}
              />
            )
          }
          case 'maximumLength': {
            const attributes =
              getValidatorAttributes<MaximumLengthValidation>(type)
            return (
              <MaximumLength
                key={type}
                attributes={
                  attributes
                    ? attributes
                    : getDefaultValues<MaximumLengthValidation>(type, {
                        maximumLength: 100
                      })
                }
                updateAttributes={a =>
                  updateValidatorAttributes<MaximumLengthValidation>(type, a)
                }
                enabled={!!attributes}
                toggleEnabled={(enabled, a) =>
                  toggleValidation(type, enabled, a)
                }
                readOnly={readOnly}
              />
            )
          }
          case 'minimumDate': {
            const attributes =
              getValidatorAttributes<MinimumDateValidation>(type)
            return (
              <MinimumDate
                key={type}
                attributes={
                  attributes
                    ? attributes
                    : getDefaultValues<MinimumDateValidation>(type, {
                        minimumDate: ''
                      })
                }
                updateAttributes={a =>
                  updateValidatorAttributes<MinimumDateValidation>(type, a)
                }
                enabled={!!attributes}
                toggleEnabled={(enabled, a) =>
                  toggleValidation(type, enabled, a)
                }
                readOnly={readOnly}
              />
            )
          }
          case 'maximumDate': {
            const attributes =
              getValidatorAttributes<MaximumDateValidation>(type)
            return (
              <MaximumDate
                key={type}
                attributes={
                  attributes
                    ? attributes
                    : getDefaultValues<MaximumDateValidation>(type, {
                        maximumDate: ''
                      })
                }
                updateAttributes={a =>
                  updateValidatorAttributes<MaximumDateValidation>(type, a)
                }
                enabled={!!attributes}
                toggleEnabled={(enabled, a) =>
                  toggleValidation(type, enabled, a)
                }
                readOnly={readOnly}
              />
            )
          }

          case 'regexp': {
            const attributes = getValidatorAttributes<RegexpValidation>(type)
            return (
              <RegExp
                key={type}
                attributes={
                  attributes
                    ? attributes
                    : getDefaultValues<RegexpValidation>(type, {regexp: ''})
                }
                updateAttributes={a =>
                  updateValidatorAttributes<RegexpValidation>(type, a)
                }
                enabled={!!attributes}
                toggleEnabled={(enabled, a) =>
                  toggleValidation(type, enabled, a)
                }
                readOnly={readOnly}
              />
            )
          }
          default:
            console.warn("I don't know how to render", type)
            return null
        }
      })}
    </NodeValidatorsContainer>
  )
}
